'use client';

import { c as _c } from "react/compiler-runtime";
import { isFundingHostPicker, isHostedFundingMethod, onrampManifest } from '@morfi/onramp-sdk';
import type { useFundingAmountDraft, useFundingInlineCheckout } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { BankTransferInstructions } from '../bank-transfers/bank-transfer-instructions';
import { FundingReceipt } from '../history/funding-receipt';
import { FundingProfile } from '../profile/funding-profile';
import { FundingLoading } from '../shared/funding-loading';
import { FundingProviderLoading } from '../shared/funding-provider-loading';
import { FundingCustomerVerificationForm } from '../verification/funding-customer-verification';
import { FundingVerification } from '../verification/funding-verification';
import { FundingAuthenticationTerms } from './funding-authentication-terms';
import { FundingCardEntry } from './funding-card-entry';
import type { Props } from './funding-checkout';
import { FundingCheckoutAmount } from './funding-checkout-amount';
import { FundingCheckoutFeedback } from './funding-checkout-feedback';
import { FundingConnectionRequired } from './funding-connection-required';
import { FundingMethodPicker } from './funding-method-picker';
import { FundingPaymentReview } from './funding-payment-review';
import { FundingProviderFrame } from './funding-provider-frame';
import { FundingHostedWidget } from './hosted/funding-hosted-widget';
export function FundingCheckoutView(t0) {
  const $ = _c(160);
  const {
    checkout,
    amountDraft,
    client,
    realtime,
    sessionProofId,
    authenticationRequested,
    authenticationBusy,
    authenticationError,
    onAcceptAuthenticationTerms,
    onAuthenticateSandbox,
    provider,
    onSessionRefresh,
    onSignIn,
    onExchange,
    currency,
    onClose,
    directPurchase,
    decimals
  } = t0;
  const t = useTranslations("onramp");
  const lowKycEligible = !directPurchase && onrampManifest.settings.guest_checkout.enabled && checkout.profileConnection?.guest === true;
  if (checkout.state === "profile") {
    let t1;
    if ($[0] !== checkout.busy || $[1] !== checkout.canDisconnect || $[2] !== checkout.loading || $[3] !== onAuthenticateSandbox) {
      t1 = onAuthenticateSandbox ? {
        onAuthenticateSandbox: async email => {
          if (!checkout.canDisconnect || checkout.busy || checkout.loading) {
            return;
          }
          await onAuthenticateSandbox(email);
        }
      } : {};
      $[0] = checkout.busy;
      $[1] = checkout.canDisconnect;
      $[2] = checkout.loading;
      $[3] = onAuthenticateSandbox;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    const t2 = checkout.loading || authenticationBusy;
    const t3 = checkout.review?.estimate.currency ?? checkout.preview.review?.estimate.currency ?? currency;
    const t4 = checkout.review?.estimate.decimals ?? checkout.preview.review?.estimate.decimals ?? decimals;
    const t5 = authenticationError || Boolean(checkout.error) && checkout.error !== "disconnect_failed";
    let t6;
    if ($[5] !== checkout.back || $[6] !== checkout.busy || $[7] !== checkout.canDisconnect || $[8] !== checkout.disconnect || $[9] !== checkout.profileConnection || $[10] !== checkout.profileUpdated || $[11] !== checkout.profileVerify || $[12] !== client || $[13] !== onSessionRefresh || $[14] !== provider || $[15] !== sessionProofId || $[16] !== t1 || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
      t6 = <FundingProfile {...t1} canDisconnect={checkout.canDisconnect} disconnectBusy={checkout.busy} onDisconnect={checkout.disconnect} connection={checkout.profileConnection} connectionPending={t2} sessionProofId={sessionProofId} client={client} provider={provider} currency={t3} decimals={t4} onBack={checkout.back} onUpdated={checkout.profileUpdated} onRefreshEligibility={onSessionRefresh} onVerify={checkout.profileVerify} verificationError={t5} />;
      $[5] = checkout.back;
      $[6] = checkout.busy;
      $[7] = checkout.canDisconnect;
      $[8] = checkout.disconnect;
      $[9] = checkout.profileConnection;
      $[10] = checkout.profileUpdated;
      $[11] = checkout.profileVerify;
      $[12] = client;
      $[13] = onSessionRefresh;
      $[14] = provider;
      $[15] = sessionProofId;
      $[16] = t1;
      $[17] = t2;
      $[18] = t3;
      $[19] = t4;
      $[20] = t5;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    return t6;
  }
  if (checkout.loading && checkout.state === "methods") {
    const t1 = Boolean(directPurchase);
    let t2;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [];
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    let t3;
    if ($[23] !== client || $[24] !== onExchange || $[25] !== t1) {
      t3 = <FundingMethodPicker directPurchase={t1} client={client} methods={t2} onExchange={onExchange} loading={true} />;
      $[23] = client;
      $[24] = onExchange;
      $[25] = t1;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    return t3;
  }
  if (checkout.method && isHostedFundingMethod(checkout.method) && checkout.state === "amount" && checkout.busy && !checkout.attemptId) {
    let t1;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FundingProviderLoading />;
      $[27] = t1;
    } else {
      t1 = $[27];
    }
    return t1;
  }
  if (checkout.loading && !checkout.paymentSignature) {
    let t1;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FundingLoading />;
      $[28] = t1;
    } else {
      t1 = $[28];
    }
    return t1;
  }
  if (isFundingHostPicker(checkout, Boolean(directPurchase), authenticationRequested)) {
    let t1;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [];
      $[29] = t1;
    } else {
      t1 = $[29];
    }
    let t2;
    if ($[30] !== client || $[31] !== onExchange) {
      t2 = <FundingMethodPicker client={client} methods={t1} onExchange={onExchange} />;
      $[30] = client;
      $[31] = onExchange;
      $[32] = t2;
    } else {
      t2 = $[32];
    }
    return t2;
  }
  if (checkout.state === "terms") {
    let t1;
    if ($[33] !== authenticationBusy || $[34] !== authenticationError || $[35] !== client || $[36] !== onAcceptAuthenticationTerms) {
      t1 = <FundingAuthenticationTerms client={client} busy={authenticationBusy} error={authenticationError} onAccept={onAcceptAuthenticationTerms} />;
      $[33] = authenticationBusy;
      $[34] = authenticationError;
      $[35] = client;
      $[36] = onAcceptAuthenticationTerms;
      $[37] = t1;
    } else {
      t1 = $[37];
    }
    return t1;
  }
  if (checkout.state === "verification") {
    const t1 = checkout.location?.country ?? undefined;
    let t2;
    if ($[38] !== authenticationError || $[39] !== checkout.back || $[40] !== client || $[41] !== onSessionRefresh || $[42] !== t1) {
      t2 = <FundingVerification client={client} sessionError={authenticationError} onContinue={onSessionRefresh} onBack={checkout.back} country={t1} />;
      $[38] = authenticationError;
      $[39] = checkout.back;
      $[40] = client;
      $[41] = onSessionRefresh;
      $[42] = t1;
      $[43] = t2;
    } else {
      t2 = $[43];
    }
    return t2;
  }
  if (checkout.state === "customer_verification" && checkout.customerVerification) {
    let t1;
    if ($[44] !== checkout.customerVerification || $[45] !== checkout.retry || $[46] !== checkout.verificationFromProfile || $[47] !== client || $[48] !== provider) {
      t1 = <FundingCustomerVerificationForm client={client} key={checkout.customerVerification.customerId} provider={provider} verification={checkout.customerVerification} onComplete={checkout.retry} fromProfile={checkout.verificationFromProfile} />;
      $[44] = checkout.customerVerification;
      $[45] = checkout.retry;
      $[46] = checkout.verificationFromProfile;
      $[47] = client;
      $[48] = provider;
      $[49] = t1;
    } else {
      t1 = $[49];
    }
    return t1;
  }
  if (checkout.state === "status" && checkout.pendingId) {
    let t1;
    if ($[50] !== checkout) {
      t1 = checkout.error ? <FundingCheckoutFeedback checkout={checkout} /> : null;
      $[50] = checkout;
      $[51] = t1;
    } else {
      t1 = $[51];
    }
    let t2;
    if ($[52] !== checkout.operation || $[53] !== checkout.pendingId) {
      t2 = checkout.operation?.id === checkout.pendingId ? {
        initialOperation: checkout.operation
      } : {};
      $[52] = checkout.operation;
      $[53] = checkout.pendingId;
      $[54] = t2;
    } else {
      t2 = $[54];
    }
    let t3;
    if ($[55] !== checkout.operation?.id || $[56] !== checkout.pendingId || $[57] !== checkout.review) {
      t3 = checkout.review && checkout.operation?.id === checkout.pendingId ? {
        quoted: {
          operationId: checkout.pendingId,
          review: checkout.review
        }
      } : {};
      $[55] = checkout.operation?.id;
      $[56] = checkout.pendingId;
      $[57] = checkout.review;
      $[58] = t3;
    } else {
      t3 = $[58];
    }
    let t4;
    if ($[59] !== realtime) {
      t4 = realtime ? {
        realtime
      } : {};
      $[59] = realtime;
      $[60] = t4;
    } else {
      t4 = $[60];
    }
    let t5;
    if ($[61] !== checkout.back || $[62] !== checkout.pendingId || $[63] !== client || $[64] !== onClose || $[65] !== onSignIn || $[66] !== t2 || $[67] !== t3 || $[68] !== t4) {
      t5 = <FundingReceipt onReturnToPurchase={onClose} progress={true} {...t2} {...t3} client={client} {...t4} operationId={checkout.pendingId} onBack={checkout.back} onSignIn={onSignIn} />;
      $[61] = checkout.back;
      $[62] = checkout.pendingId;
      $[63] = client;
      $[64] = onClose;
      $[65] = onSignIn;
      $[66] = t2;
      $[67] = t3;
      $[68] = t4;
      $[69] = t5;
    } else {
      t5 = $[69];
    }
    let t6;
    if ($[70] !== checkout || $[71] !== t) {
      t6 = checkout.canResumeChallenge ? <AnimatedButton variant="secondary" loading={checkout.busy} disabled={checkout.busy} onClick={() => {
        checkout.resumeChallenge();
      }}>{t("resume_verification")}</AnimatedButton> : null;
      $[70] = checkout;
      $[71] = t;
      $[72] = t6;
    } else {
      t6 = $[72];
    }
    let t7;
    if ($[73] !== t1 || $[74] !== t5 || $[75] !== t6) {
      t7 = <>{t1}{t5}{t6}</>;
      $[73] = t1;
      $[74] = t5;
      $[75] = t6;
      $[76] = t7;
    } else {
      t7 = $[76];
    }
    return t7;
  }
  if (checkout.state === "connection_required" || checkout.state === "unavailable" || checkout.state === "methods" && !checkout.error && (checkout.methods.length === 0 || checkout.authenticationAvailable)) {
    const t1 = Boolean(directPurchase);
    let t2;
    if ($[77] !== checkout) {
      t2 = id => {
        checkout.deleteSavedCard(id);
      };
      $[77] = checkout;
      $[78] = t2;
    } else {
      t2 = $[78];
    }
    const t3 = checkout.state !== "unavailable" && checkout.authenticationAvailable;
    let t4;
    if ($[79] !== authenticationBusy || $[80] !== authenticationError || $[81] !== checkout.authenticationReason || $[82] !== checkout.location || $[83] !== checkout.retry || $[84] !== t3) {
      t4 = <FundingConnectionRequired reason={checkout.authenticationReason} location={checkout.location} onRefresh={checkout.retry} canAuthenticate={t3} busy={authenticationBusy} error={authenticationError} />;
      $[79] = authenticationBusy;
      $[80] = authenticationError;
      $[81] = checkout.authenticationReason;
      $[82] = checkout.location;
      $[83] = checkout.retry;
      $[84] = t3;
      $[85] = t4;
    } else {
      t4 = $[85];
    }
    let t5;
    if ($[86] !== checkout.busy || $[87] !== checkout.methods || $[88] !== checkout.openProfile || $[89] !== checkout.savedCards || $[90] !== checkout.select || $[91] !== checkout.selectSavedCard || $[92] !== checkout.unavailableBankMethods || $[93] !== checkout.unavailableMethods || $[94] !== client || $[95] !== lowKycEligible || $[96] !== onExchange || $[97] !== t1 || $[98] !== t2 || $[99] !== t4) {
      t5 = <FundingMethodPicker directPurchase={t1} client={client} methods={checkout.methods} unavailableBankMethods={checkout.unavailableBankMethods} unavailableMethods={checkout.unavailableMethods} lowKycEligible={lowKycEligible} onSelect={checkout.select} onExchange={onExchange} onProfile={checkout.openProfile} feedbackAfterMethods={true} savedCards={checkout.savedCards} busy={checkout.busy} onSelectCard={checkout.selectSavedCard} onRemoveCard={t2} providerFeedback={t4} />;
      $[86] = checkout.busy;
      $[87] = checkout.methods;
      $[88] = checkout.openProfile;
      $[89] = checkout.savedCards;
      $[90] = checkout.select;
      $[91] = checkout.selectSavedCard;
      $[92] = checkout.unavailableBankMethods;
      $[93] = checkout.unavailableMethods;
      $[94] = client;
      $[95] = lowKycEligible;
      $[96] = onExchange;
      $[97] = t1;
      $[98] = t2;
      $[99] = t4;
      $[100] = t5;
    } else {
      t5 = $[100];
    }
    return t5;
  }
  if (checkout.state === "connect" && !checkout.paymentSignature) {
    let t1;
    if ($[101] !== checkout || $[102] !== provider) {
      t1 = <FundingProviderFrame key={checkout.state} provider={provider} checkout={checkout} />;
      $[101] = checkout;
      $[102] = provider;
      $[103] = t1;
    } else {
      t1 = $[103];
    }
    return t1;
  }
  if (checkout.state === "bank_transfer_instructions" && checkout.pendingId) {
    let t1;
    if ($[104] !== checkout.pendingId || $[105] !== checkout.viewPaymentStatus || $[106] !== client || $[107] !== onClose) {
      t1 = <BankTransferInstructions client={client} operationId={checkout.pendingId} onDismiss={onClose} onViewStatus={checkout.viewPaymentStatus} />;
      $[104] = checkout.pendingId;
      $[105] = checkout.viewPaymentStatus;
      $[106] = client;
      $[107] = onClose;
      $[108] = t1;
    } else {
      t1 = $[108];
    }
    return t1;
  }
  if (checkout.method && isHostedFundingMethod(checkout.method) && (checkout.state === "amount" || checkout.state === "payment")) {
    let t1;
    if ($[109] !== amountDraft || $[110] !== checkout || $[111] !== provider) {
      t1 = <FundingCheckoutAmount checkout={checkout} amountDraft={amountDraft} provider={provider} />;
      $[109] = amountDraft;
      $[110] = checkout;
      $[111] = provider;
      $[112] = t1;
    } else {
      t1 = $[112];
    }
    const t2 = checkout.state !== "payment";
    const t3 = checkout.state !== "payment";
    let t4;
    if ($[113] !== checkout || $[114] !== client || $[115] !== provider) {
      t4 = checkout.paymentSignature ? <FundingHostedWidget key={checkout.attemptId} client={client} provider={provider} checkout={checkout} /> : null;
      $[113] = checkout;
      $[114] = client;
      $[115] = provider;
      $[116] = t4;
    } else {
      t4 = $[116];
    }
    let t5;
    if ($[117] !== t2 || $[118] !== t3 || $[119] !== t4) {
      t5 = <div hidden={t2} inert={t3}>{t4}</div>;
      $[117] = t2;
      $[118] = t3;
      $[119] = t4;
      $[120] = t5;
    } else {
      t5 = $[120];
    }
    let t6;
    if ($[121] !== t1 || $[122] !== t5) {
      t6 = <div className="flex flex-col gap-4">{t1}{t5}</div>;
      $[121] = t1;
      $[122] = t5;
      $[123] = t6;
    } else {
      t6 = $[123];
    }
    return t6;
  }
  if (checkout.state === "card_entry") {
    let t1;
    if ($[124] !== checkout) {
      t1 = <FundingCheckoutFeedback checkout={checkout} />;
      $[124] = checkout;
      $[125] = t1;
    } else {
      t1 = $[125];
    }
    let t2;
    if ($[126] !== checkout || $[127] !== provider) {
      t2 = <FundingCardEntry provider={provider} checkout={checkout} />;
      $[126] = checkout;
      $[127] = provider;
      $[128] = t2;
    } else {
      t2 = $[128];
    }
    let t3;
    if ($[129] !== t1 || $[130] !== t2) {
      t3 = <>{t1}{t2}</>;
      $[129] = t1;
      $[130] = t2;
      $[131] = t3;
    } else {
      t3 = $[131];
    }
    return t3;
  }
  let t1;
  if ($[132] !== checkout || $[133] !== provider) {
    t1 = <FundingPaymentReview provider={provider} checkout={checkout} />;
    $[132] = checkout;
    $[133] = provider;
    $[134] = t1;
  } else {
    t1 = $[134];
  }
  let t2;
  if ($[135] !== checkout) {
    t2 = !checkout.amountEntry && !(checkout.method === "sepa" && checkout.state === "review") ? <FundingCheckoutFeedback checkout={checkout} /> : null;
    $[135] = checkout;
    $[136] = t2;
  } else {
    t2 = $[136];
  }
  let t3;
  if ($[137] !== amountDraft || $[138] !== checkout || $[139] !== provider) {
    t3 = <FundingCheckoutAmount checkout={checkout} amountDraft={amountDraft} provider={provider} />;
    $[137] = amountDraft;
    $[138] = checkout;
    $[139] = provider;
    $[140] = t3;
  } else {
    t3 = $[140];
  }
  let t4;
  if ($[141] !== checkout || $[142] !== provider) {
    t4 = checkout.state === "connect" ? <FundingProviderFrame key="connect" provider={provider} checkout={checkout} /> : null;
    $[141] = checkout;
    $[142] = provider;
    $[143] = t4;
  } else {
    t4 = $[143];
  }
  let t5;
  if ($[144] !== checkout || $[145] !== provider) {
    t5 = checkout.state === "challenge" ? <FundingProviderFrame key="challenge" provider={provider} checkout={checkout} /> : null;
    $[144] = checkout;
    $[145] = provider;
    $[146] = t5;
  } else {
    t5 = $[146];
  }
  let t6;
  if ($[147] !== checkout || $[148] !== client || $[149] !== directPurchase || $[150] !== lowKycEligible || $[151] !== onExchange) {
    t6 = checkout.state === "methods" ? <FundingMethodPicker directPurchase={Boolean(directPurchase)} client={client} methods={checkout.methods} unavailableBankMethods={checkout.unavailableBankMethods} unavailableMethods={checkout.unavailableMethods} lowKycEligible={lowKycEligible} onSelect={checkout.select} onExchange={onExchange} onProfile={checkout.openProfile} savedCards={checkout.savedCards} busy={checkout.busy} onSelectCard={checkout.selectSavedCard} onRemoveCard={id_0 => {
      checkout.deleteSavedCard(id_0);
    }} /> : null;
    $[147] = checkout;
    $[148] = client;
    $[149] = directPurchase;
    $[150] = lowKycEligible;
    $[151] = onExchange;
    $[152] = t6;
  } else {
    t6 = $[152];
  }
  let t7;
  if ($[153] !== t1 || $[154] !== t2 || $[155] !== t3 || $[156] !== t4 || $[157] !== t5 || $[158] !== t6) {
    t7 = <div className="flex flex-col gap-4">{t1}{t2}{t3}{t4}{t5}{t6}</div>;
    $[153] = t1;
    $[154] = t2;
    $[155] = t3;
    $[156] = t4;
    $[157] = t5;
    $[158] = t6;
    $[159] = t7;
  } else {
    t7 = $[159];
  }
  return t7;
}